import { useTranslation } from "react-i18next";
import { Box, Typography, Chip } from "@mui/material";

import { ApplyCriteriaEnum } from "../../../../../enum/jobEnums";
import { CreateTalentJob } from "../../../../../types/createJobSchema";
import { ListItemIcon } from "../../../../../assets/icons/go-hire-page-icons";

const SubmissionInfo = ({ jobDetails }: { jobDetails: CreateTalentJob }) => {
  const { t, i18n } = useTranslation("goHire", { keyPrefix: "createJobPage.previewAndPublishJob" });
  const { t: tJobDetails } = useTranslation("goHire", { keyPrefix: "createJobPage.jobDetails" });
  const { t: tIntroductoryVideoLength } = useTranslation("goHire", { keyPrefix: "enum.introductoryVideoLength" });
  const { t: tHireEnums } = useTranslation("goHire", { keyPrefix: "enum" });
  const { t: tLanguage } = useTranslation("goHire", { keyPrefix: "enum.language" });

  const submissionInfo: (string | { label: string; values: string[] })[] = [];
  if (jobDetails.isLanguagesSpokenActive && jobDetails.languagesSpoken?.length) {
    submissionInfo.push({
      label: tJobDetails("languagesSpoken"),
      values: jobDetails.languagesSpoken.map((lang) => tLanguage(lang)),
    });
  }

  if (jobDetails.isClubsWorkedInActive && jobDetails.clubsWorkedIn?.length) {
    submissionInfo.push({
      label: tJobDetails("specifyClubsAcademiesWorkedIn"),
      values: jobDetails.clubsWorkedIn.map((club) => (i18n.language === "ar" ? club.name_ar : club.name_en)),
    });
  }

  if (jobDetails.willingnessToRelocate) {
    submissionInfo.push(tJobDetails("willingnessToRelocate"));
  }

  if (jobDetails.isIntroductoryVideoActive && jobDetails.introductoryVideo) {
    submissionInfo.push(
      `${tJobDetails("submitIntroductoryVideo")}: ${jobDetails.introductoryVideo.description} (${tIntroductoryVideoLength(
        jobDetails.introductoryVideo.length,
      )})`,
    );
  }

  if (jobDetails.majorInjury) {
    submissionInfo.push(tJobDetails("anyMajorInjury"));
  }

  const whoApplyCriteria: string[] = [];
  if (jobDetails[ApplyCriteriaEnum.onlyVerifiedFootballers])
    whoApplyCriteria.push(tHireEnums(`applyCriteria.${ApplyCriteriaEnum.onlyVerifiedFootballers}`));
  if (jobDetails[ApplyCriteriaEnum.onlyWithTestimonials])
    whoApplyCriteria.push(tHireEnums(`applyCriteria.${ApplyCriteriaEnum.onlyWithTestimonials}`));
  if (jobDetails[ApplyCriteriaEnum.profileCompletionAbove80])
    whoApplyCriteria.push(tHireEnums(`applyCriteria.${ApplyCriteriaEnum.profileCompletionAbove80}`));

  return (
    <Box component="section" sx={styles.container}>
      {submissionInfo.length > 0 && (
        <Box component="ul" sx={styles.listContainer}>
          <Typography sx={styles.listTitle}>{t("requiredSubmissionInfo")}</Typography>

          {submissionInfo.map((info, index) => {
            const isString = typeof info === "string";

            return (
              <Box
                component="li"
                key={isString ? info : info.label + index}
                sx={{ ...styles.listItemsContainer, alignItems: isString ? "center" : "flex-start" }}
              >
                <ListItemIcon sx={!isString ? { mt: "4px" } : {}} />
                {isString ? (
                  <Typography sx={styles.listText}>{info}</Typography>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <Typography sx={styles.listText}>{info.label}:</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                      {info.values.map((val) => (
                        <Chip key={val} label={val} size="small" variant="outlined" sx={styles.chip} />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
      {whoApplyCriteria.length > 0 && (
        <Box component="ul" sx={styles.listContainer}>
          <Typography sx={styles.listTitle}>{t("whoCanApply")}</Typography>

          {whoApplyCriteria.map((info) => (
            <Box component="li" key={info} sx={styles.listItemsContainer}>
              <ListItemIcon />
              <Typography sx={styles.listText}>{info}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SubmissionInfo;

const styles = {
  container: {
    width: "100%",
    display: "flex",
    gap: "24px",
  },
  listTitle: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  listContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  listItemsContainer: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  listText: {
    color: "var(--Dark-Mode---100, #1E2021)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
  chip: {
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontWeight: 500,
    color: "var(--Dark-Mode---100, #1E2021)",
    borderColor: "var(--Dark-Mode-Border, #EAECF0)",
    height: "24px",
    borderRadius: "6px",
  },
};
